import { GatewayAPIClient, GatewayAPIError, gatewayAPIClient } from "../../app/api/client";

export interface ContactSummary {
  user_id: string;
  display_name: string;
  nickname?: string;
  username?: string;
  status_text?: string;
  avatar_url?: string;
  created_at: string;
}

type ContactAPIClient = Pick<GatewayAPIClient, "request">;

interface AddContactRequest {
  user_id: string;
}

function getClient(client?: ContactAPIClient): ContactAPIClient {
  return client ?? gatewayAPIClient;
}

function invalidResponse(payload: unknown): GatewayAPIError {
  return new GatewayAPIError(500, "invalid_response", payload);
}

function requireContactUserID(value: string, fieldName = "user_id"): string {
  const normalized = value.trim();
  if (normalized === "") {
    throw new Error(`${fieldName} must not be empty`);
  }
  if (!normalized.startsWith("usr_")) {
    throw new Error(`${fieldName} must be a canonical user id`);
  }
  if (normalized.includes("/") || /\s/.test(normalized)) {
    throw new Error(`${fieldName} contains invalid path separator or whitespace`);
  }
  return normalized;
}

function requireStringField(payload: Record<string, unknown>, fieldName: string): string {
  const value = payload[fieldName];
  if (typeof value !== "string") {
    throw invalidResponse(payload);
  }
  return value.trim();
}

function optionalStringField(payload: Record<string, unknown>, fieldName: string): string | undefined {
  const value = payload[fieldName];
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value !== "string") {
    throw invalidResponse(payload);
  }
  const normalized = value.trim();
  return normalized === "" ? undefined : normalized;
}

function normalizeCreatedAt(payload: Record<string, unknown>): string {
  const createdAt = requireStringField(payload, "created_at");
  if (createdAt === "" || Number.isNaN(Date.parse(createdAt))) {
    throw invalidResponse(payload);
  }
  return createdAt;
}

export function normalizeContactSummary(payload: unknown): ContactSummary {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw invalidResponse(payload);
  }

  const record = payload as Record<string, unknown>;
  return {
    user_id: requireContactUserID(requireStringField(record, "user_id")),
    display_name: requireStringField(record, "display_name"),
    nickname: optionalStringField(record, "nickname"),
    username: optionalStringField(record, "username"),
    status_text: optionalStringField(record, "status_text"),
    avatar_url: optionalStringField(record, "avatar_url"),
    created_at: normalizeCreatedAt(record),
  };
}

export async function listContacts(client?: ContactAPIClient): Promise<ContactSummary[]> {
  const response = await getClient(client).request<unknown>({
    method: "GET",
    path: "/v1/contacts",
  });

  if (!Array.isArray(response)) {
    throw invalidResponse(response);
  }
  return response.map((item) => normalizeContactSummary(item));
}

export async function addContact(userID: string, client?: ContactAPIClient): Promise<ContactSummary> {
  const payload: AddContactRequest = {
    user_id: requireContactUserID(userID),
  };

  const response = await getClient(client).request<unknown>({
    method: "POST",
    path: "/v1/contacts",
    body: payload,
  });
  return normalizeContactSummary(response);
}

export async function removeContact(userID: string, client?: ContactAPIClient): Promise<void> {
  const normalizedUserID = requireContactUserID(userID);
  await getClient(client).request<void>({
    method: "DELETE",
    path: `/v1/contacts/${encodeURIComponent(normalizedUserID)}`,
  });
}
